import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/axios';
import type { AxiosError } from 'axios';
import type { TenantResponseDTO } from '@/types/Tenant';
import { useLocalStorage } from '@/contexts/LocalStorageContext';

interface User {
  email: string;
  exp: number;
  lastName: string;
  id: string;
  name: string;
  role: string;
  tenant?: TenantResponseDTO;
  warehouseId: number;
  claims?: string[];
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthContextType {
  user: User | null;
  tenant: TenantResponseDTO | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginUser: (credentials: LoginCredentials) => void;
  loginMutation: {
    isPending: boolean;
    error: AxiosError | null;
  };
  logout: () => void;
  isSuperAdmin: boolean;
  hasClaim: (claimType: string) => boolean;
  warehouse: number | null;
  setWarehouse: (warehouse: number) => void;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const JWT_TOKEN_KEY = 'jwtToken';

// Helper function to check if we're in browser environment
const isBrowser = () => typeof window !== 'undefined';

const decodeJwt = (token: string) => {
  try {
    const [headerEncoded, payloadEncoded] = token.split('.').slice(0, 2);

    const header = JSON.parse(
      atob(headerEncoded.replace(/-/g, '+').replace(/_/g, '/'))
    );
    const payload = JSON.parse(
      atob(payloadEncoded.replace(/-/g, '+').replace(/_/g, '/'))
    );

    // Parse tenant object if available
    let tenant: TenantResponseDTO | undefined;
    if (payload.tenant) {
      try {
        tenant =
          typeof payload.tenant === 'string'
            ? JSON.parse(payload.tenant)
            : payload.tenant;
      } catch (error) {
        console.error('Error parsing tenant from JWT:', error);
      }
    }

    // Parse claims (JWT may have single string or array of strings)
    let claims: string[] = [];
    if (payload.Claims != null) {
      claims = Array.isArray(payload.Claims)
        ? payload.Claims
        : [payload.Claims];
    }

    return {
      header,
      payload: {
        email: payload.unique_name,
        exp: payload.exp,
        lastName: payload.family_name,
        id: payload.sub,
        name: payload.name,
        role: payload.Role,
        tenant,
        warehouseId: payload.WarehouseId,
        claims,
      },
    };
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.payload.exp) return true;

  const currentTime = Date.now() / 1000;
  return decoded.payload.exp < currentTime;
};

export const isSuperAdminFromStorage = () => {
  if (!isBrowser()) {
    return false;
  }

  const token = localStorage.getItem(JWT_TOKEN_KEY);
  if (!token) {
    return false;
  }

  const decoded = decodeJwt(token);
  return decoded?.payload?.role === 'SuperAdmin';
};

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasToken, setHasToken] = useState(false);
  const [warehouse, setWarehouse] = useState<number | null>(null);
  const { getItem, setItem } = useLocalStorage();
  const storedWarehouse = getItem<number>('warehouse');

  const queryClient = useQueryClient();

  const applyRefreshedToken = useCallback((token: string) => {
    try {
      if (isBrowser()) {
        localStorage.setItem(JWT_TOKEN_KEY, token);
      }
      const decoded = decodeJwt(token);
      if (decoded?.payload) {
        setUser(decoded.payload);
      }
    } catch (error) {
      console.error('Error applying refreshed token:', error);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const { data } = await api.post<{ token: string; validTo: string }>(
      '/auth/refresh'
    );
    if (!data?.token) {
      return;
    }
    applyRefreshedToken(data.token);
    queryClient.invalidateQueries();
  }, [applyRefreshedToken, queryClient]);

  useEffect(() => {
    const _warehouse = storedWarehouse
      ? storedWarehouse
      : user
        ? user.warehouseId
        : null;

    setWarehouse(Number(_warehouse));
    setItem('warehouse', Number(_warehouse));
  }, [user, storedWarehouse]);

  useEffect(() => {
    const initializeAuth = () => {
      api.defaults.headers.common['x-warehouse'] = warehouse;

      try {
        const token = localStorage.getItem(JWT_TOKEN_KEY);

        if (token) {
          setHasToken(true);
          if (!isTokenExpired(token)) {
            const decoded = decodeJwt(token);
            if (decoded && decoded.payload) {
              setUser(decoded.payload);
            }
          }
        }
      } catch (error) {
        if (isBrowser()) {
          localStorage.removeItem(JWT_TOKEN_KEY);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [warehouse]);

  const refreshTokenQuery = useQuery({
    queryKey: ['auth', 'refresh'],
    queryFn: async () => {
      const { data } = await api.post<{ token: string; validTo: string }>(
        '/auth/refresh'
      );
      return data;
    },
    enabled: hasToken && isBrowser(),
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (refreshTokenQuery.data?.token) {
      applyRefreshedToken(refreshTokenQuery.data.token);
    }
  }, [refreshTokenQuery.data, applyRefreshedToken]);

  useEffect(() => {
    try {
      api.defaults.headers.common['x-warehouse'] = warehouse;
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Error initializing warehouse:', error);
    }
  }, [warehouse]);

  const setAuthToken = (token: string) => {
    try {
      if (isTokenExpired(token)) {
        throw new Error('Token is expired');
      }

      if (isBrowser()) {
        localStorage.setItem(JWT_TOKEN_KEY, token);
      }
      const decoded = decodeJwt(token);

      if (decoded && decoded.payload) {
        setUser(decoded.payload);
      }
    } catch (error) {
      console.error('Error during token setting:', error);
      throw error;
    }
  };

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => {
      return api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
      });
    },
    onSuccess: ({ data }) => {
      const token = data.data.token;

      if (token) {
        setAuthToken(token);
        setHasToken(true);
      } else {
        throw new Error('No access token received');
      }
    },
    onError: (error: AxiosError) => {
      console.error('Login error:', error);
    },
  });

  const loginUser = (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials);
  };

  const logout = () => {
    setHasToken(false);
    if (isBrowser()) {
      localStorage.removeItem(JWT_TOKEN_KEY);
      setItem('warehouse', null);
    }
    setUser(null);
    queryClient.clear();

    if (isBrowser()) {
      window.location.replace('/login');
    }
  };

  const isSuperAdmin = useMemo(() => user?.role === 'SuperAdmin', [user]);
  const tenant = useMemo(() => user?.tenant, [user]);
  const hasClaim = useMemo(
    () => (claimType: string) =>
      isSuperAdmin || (user?.claims?.includes(claimType) ?? false),
    [user?.claims, isSuperAdmin]
  );

  const value = {
    user,
    tenant,
    isAuthenticated: !!user,
    isLoading,
    loginUser,
    isSuperAdmin,
    hasClaim,
    warehouse,
    setWarehouse,
    loginMutation: {
      isPending: loginMutation.isPending,
      error: loginMutation.error,
    },
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
